import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PrinterIcon, IdentificationCardIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

/**
 * Guest Registration Card (GRC) printable form.
 * - Blank mode: prints an empty government-compliant card (walk-in / advance stock).
 * - Room mode: pick a room with an active reservation → auto-populates guest + stay
 *   details, then prints a filled card that the guest just needs to sign.
 * Header always renders the currently-selected property (name, GSTIN, address).
 */
export default function GuestRegistrationCard({ open, onClose }) {
  const { activeId, active } = useProperty();
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [platform, setPlatform] = useState({});
  const [mode, setMode] = useState("filled"); // 'filled' | 'blank'
  const [roomId, setRoomId] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [rr, rs, gs, pl] = await Promise.all([
        api.get("/rooms", { params: { property_id: activeId } }),
        api.get("/reservations", { params: { property_id: activeId } }),
        api.get("/guests"),
        api.get(`/settings/property/${activeId}`).catch(() => ({ data: { effective: {} } })),
      ]);
      setRooms(rr.data || []);
      setReservations(rs.data || []);
      setGuests(gs.data || []);
      setPlatform(pl?.data?.effective || {});
      setRoomId("");
      setMode("filled");
    })();
  }, [open, activeId]);

  // Rooms that have an active (booked / checked_in) reservation right now
  const activeRoomOptions = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const activeResByRoom = {};
    (reservations || []).forEach((r) => {
      if (!["booked", "checked_in"].includes(r.status)) return;
      if (r.check_out < today) return; // past
      if (!activeResByRoom[r.room_id]) activeResByRoom[r.room_id] = r;
    });
    return (rooms || [])
      .filter((r) => activeResByRoom[r.id])
      .map((r) => ({ room: r, reservation: activeResByRoom[r.id] }));
  }, [rooms, reservations]);

  const selectedRow = activeRoomOptions.find((row) => row.room.id === roomId);
  const selectedReservation = selectedRow?.reservation;
  const selectedGuest = selectedReservation ? guests.find((g) => g.id === selectedReservation.guest_id) : null;
  const selectedRoom = selectedRow?.room;

  const handlePrint = () => {
    if (mode === "filled" && !selectedReservation) {
      return toast.error("Pick a room with an active reservation, or switch to Blank form");
    }
    const propName = active?.name || platform.group_name || "Hotel";
    const propAddress = active?.address || platform.address || "";
    const propCity = [active?.city, active?.state].filter(Boolean).join(", ");
    const propPhone = active?.phone || "";
    const propGst = platform.gst_number || "";
    const logo = platform.logo_url || null;
    const g = mode === "filled" ? (selectedGuest || {}) : {};
    const r = mode === "filled" ? (selectedReservation || {}) : {};
    const room = mode === "filled" ? (selectedRoom || {}) : {};
    const nights = mode === "filled" && r.check_in && r.check_out
      ? Math.max(1, Math.round((new Date(r.check_out) - new Date(r.check_in)) / 86400000))
      : "";
    const html = buildGrcHtml({
      propName, propAddress, propCity, propPhone, propGst, logo,
      guest: g, reservation: r, room, nights, blank: mode === "blank",
    });
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return toast.error("Popup blocked — allow popups then try again");
    w.document.write(html);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="grc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><IdentificationCardIcon size={18} weight="duotone" className="text-primary" /> Guest Registration Card</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            <p className="label-overline text-muted-foreground">Header will show</p>
            <p className="font-display text-lg leading-tight mt-1">{active?.name || "—"}</p>
            <p className="text-muted-foreground">{active?.address || "—"}, {[active?.city, active?.state].filter(Boolean).join(", ")}</p>
            {platform.gst_number && <p className="text-muted-foreground">GSTIN: <span className="font-mono-plex">{platform.gst_number}</span></p>}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mode</Label>
            <div className="mt-2 flex gap-2">
              <label className="flex-1 flex items-center gap-2 rounded-md border border-border p-2 cursor-pointer" data-testid="grc-mode-filled">
                <Checkbox checked={mode === "filled"} onCheckedChange={() => setMode("filled")} />
                <span className="text-sm">Filled from booking</span>
              </label>
              <label className="flex-1 flex items-center gap-2 rounded-md border border-border p-2 cursor-pointer" data-testid="grc-mode-blank">
                <Checkbox checked={mode === "blank"} onCheckedChange={() => setMode("blank")} />
                <span className="text-sm">Blank form</span>
              </label>
            </div>
          </div>
          {mode === "filled" && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Select room / active booking</Label>
              <Select value={roomId} onValueChange={setRoomId}>
                <SelectTrigger className="mt-1" data-testid="grc-room-select"><SelectValue placeholder={activeRoomOptions.length ? "Pick a room" : "No active bookings"} /></SelectTrigger>
                <SelectContent>
                  {activeRoomOptions.map((row) => {
                    const gname = guests.find((x) => x.id === row.reservation.guest_id)?.name || "Guest";
                    return (
                      <SelectItem key={row.room.id} value={row.room.id} data-testid={`grc-room-opt-${row.room.number}`}>
                        #{row.room.number} · {row.room.room_type} · {gname} · {row.reservation.check_in} → {row.reservation.check_out}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedGuest && (
                <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="grc-preview">
                  <p className="font-medium text-primary">{selectedGuest.name} · {selectedGuest.phone}</p>
                  {selectedGuest.email && <p className="text-muted-foreground">{selectedGuest.email}</p>}
                  <p className="text-muted-foreground">Room #{selectedRoom?.number} · {selectedReservation?.adults || 1} adult(s), {selectedReservation?.children || 0} child(ren)</p>
                  <p className="text-muted-foreground">{selectedReservation?.check_in} → {selectedReservation?.check_out}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          <Button type="button" onClick={handlePrint} data-testid="grc-print-btn">
            <PrinterIcon size={14} className="mr-1" /> Print {mode === "blank" ? "blank form" : "GRC"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function _esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function _val(v, blank) {
  if (blank) return "&nbsp;";
  const s = String(v ?? "").trim();
  return s ? _esc(s) : "&nbsp;";
}

function buildGrcHtml({ propName, propAddress, propCity, propPhone, propGst, logo, guest, reservation, room, nights, blank }) {
  const now = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const idType = (guest.id_type || "").toUpperCase();
  return `<!doctype html><html><head><title>${_esc(propName)} · Guest Registration Card</title>
<meta charset="utf-8" />
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: 'Inter', system-ui, sans-serif; color: #16241B; margin: 24px; font-size: 11px; }
  .header { display: flex; align-items: center; gap: 16px; border-bottom: 3px double #2F5D50; padding-bottom: 12px; margin-bottom: 12px; }
  .header img { height: 56px; width: auto; object-fit: contain; }
  .header .titles { flex: 1; }
  .header h1 { font-family: 'Playfair Display', 'Times New Roman', serif; font-size: 22px; color: #2F5D50; margin: 0; letter-spacing: -0.01em; }
  .header .meta { font-size: 10px; color: #4E534D; margin-top: 4px; line-height: 1.5; }
  .form-title { text-align: center; font-family: 'Playfair Display', serif; font-size: 16px; color: #2F5D50; margin: 10px 0 14px; letter-spacing: 0.06em; text-transform: uppercase; }
  .row { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px 16px; margin-bottom: 8px; }
  .row.three { grid-template-columns: repeat(3, 1fr); }
  .row.four { grid-template-columns: repeat(4, 1fr); }
  .field { display: flex; flex-direction: column; }
  .field label { font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #4E534D; margin-bottom: 2px; }
  .field .val { border-bottom: 1px solid #4E534D; min-height: 18px; padding: 2px 4px; font-size: 12px; }
  .field.full { grid-column: 1 / -1; }
  .section-title { font-family: 'Playfair Display', serif; font-size: 12px; color: #2F5D50; text-transform: uppercase; letter-spacing: 0.1em; margin: 14px 0 6px; border-bottom: 1px solid #C8A24B; padding-bottom: 3px; }
  .declaration { border: 1px solid #4E534D; padding: 10px; margin-top: 14px; font-size: 10px; line-height: 1.5; }
  .sign-row { display: grid; grid-template-columns: 2fr 1fr; gap: 24px; margin-top: 24px; }
  .sign-box { text-align: center; }
  .sign-line { border-top: 1px solid #4E534D; margin-top: 32px; padding-top: 4px; font-size: 10px; color: #4E534D; }
  .footer { margin-top: 20px; text-align: right; font-size: 9px; color: #4E534D; letter-spacing: 0.05em; text-transform: uppercase; }
  @page { margin: 10mm; }
  @media print { button { display: none; } }
</style></head><body>
<div class="header">
  ${logo ? `<img src="${_esc(logo)}" alt="" />` : ""}
  <div class="titles">
    <h1>${_esc(propName)}</h1>
    <div class="meta">
      ${_esc(propAddress)}<br/>
      ${_esc(propCity)}${propPhone ? " · Tel: " + _esc(propPhone) : ""}${propGst ? " · GSTIN: " + _esc(propGst) : ""}
    </div>
  </div>
</div>
<div class="form-title">Guest Registration Card</div>

<div class="row three">
  <div class="field"><label>GRC No.</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Room No.</label><div class="val">${_val(room.number, blank)}</div></div>
  <div class="field"><label>Room Type</label><div class="val">${_val(room.room_type, blank)}</div></div>
</div>

<div class="section-title">Guest details</div>
<div class="row">
  <div class="field full"><label>Full name (as per ID)</label><div class="val">${_val(guest.name, blank)}</div></div>
</div>
<div class="row">
  <div class="field"><label>Phone</label><div class="val">${_val(guest.phone, blank)}</div></div>
  <div class="field"><label>Email</label><div class="val">${_val(guest.email, blank)}</div></div>
</div>
<div class="row">
  <div class="field"><label>Nationality</label><div class="val">${_val(guest.nationality, blank)}</div></div>
  <div class="field"><label>Occupation</label><div class="val">&nbsp;</div></div>
</div>
<div class="row">
  <div class="field full"><label>Address</label><div class="val">${_val(guest.address, blank)}</div></div>
</div>
<div class="row three">
  <div class="field"><label>ID type</label><div class="val">${_val(idType, blank)}</div></div>
  <div class="field"><label>ID number</label><div class="val">${_val(guest.id_number, blank)}</div></div>
  <div class="field"><label>ID issued at / valid till</label><div class="val">&nbsp;</div></div>
</div>

<div class="section-title">Stay details</div>
<div class="row four">
  <div class="field"><label>Check-in date</label><div class="val">${_val(reservation.check_in, blank)}</div></div>
  <div class="field"><label>Check-out date</label><div class="val">${_val(reservation.check_out, blank)}</div></div>
  <div class="field"><label>No. of nights</label><div class="val">${_val(nights, blank)}</div></div>
  <div class="field"><label>Meal plan</label><div class="val">${_val(reservation.plan_code, blank)}</div></div>
</div>
<div class="row four">
  <div class="field"><label>Adults</label><div class="val">${_val(reservation.adults, blank)}</div></div>
  <div class="field"><label>Children</label><div class="val">${_val(reservation.children, blank)}</div></div>
  <div class="field"><label>Rate / night (₹)</label><div class="val">${_val(reservation.rate_per_night, blank)}</div></div>
  <div class="field"><label>Total (₹)</label><div class="val">${_val(reservation.total_amount, blank)}</div></div>
</div>
<div class="row three">
  <div class="field"><label>Purpose of visit</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Arriving from</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Next destination</label><div class="val">&nbsp;</div></div>
</div>
<div class="row three">
  <div class="field"><label>Vehicle no.</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Emergency contact</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Payment mode</label><div class="val">&nbsp;</div></div>
</div>

<div class="section-title">Accompanying guests</div>
<div class="row four">
  <div class="field"><label>Name</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Relation</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Age</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>ID no.</label><div class="val">&nbsp;</div></div>
</div>
<div class="row four">
  <div class="field"><label>Name</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Relation</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>Age</label><div class="val">&nbsp;</div></div>
  <div class="field"><label>ID no.</label><div class="val">&nbsp;</div></div>
</div>

<div class="declaration">
  I hereby declare that the information given above is true and correct to the best of my knowledge. I have read and agreed to the terms and conditions of ${_esc(propName)}. I understand that the hotel reserves the right to refuse service or evict any guest for misconduct. I will settle all dues before departure.
</div>

<div class="sign-row">
  <div class="sign-box"><div class="sign-line">Guest signature</div></div>
  <div class="sign-box"><div class="sign-line">Authorised by</div></div>
</div>

<div class="footer">Printed ${_esc(now)} · ${_esc(propName)}</div>
<script>window.addEventListener('load', () => { setTimeout(() => { window.focus(); window.print(); }, 300); });</script>
</body></html>`;
}
